/**
 * Grant history — the tenant-wide grant ledger, newest first (Wave 3b of the
 * authz rebuild). This is the ABAC-014 precondition surface: before admins go
 * blind to business data, every access change must be loudly visible, and an
 * admin granting THEMSELVES access must be unmissable — self-grant rows are
 * tinted and badged.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Label, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { History } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ResolvedId, useAdminNames } from '../nameResolution';
import { useListGrantEvents } from './hooks';
/** Debounces a fast-changing value (SAD-104 filter inputs) so a filter
 * refetch doesn't fire on every keystroke. */ function useDebounced(value, delayMs = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(()=>{
        const id = setTimeout(()=>setDebounced(value), delayMs);
        return ()=>clearTimeout(id);
    }, [
        value,
        delayMs
    ]);
    return debounced;
}
function formatTimestamp(ts) {
    if (!ts || Number(ts.seconds) === 0) return '—';
    return new Date(Number(ts.seconds) * 1000).toLocaleString();
}
// Machine provenance: grants the SYSTEM minted (program-creator seeds, roster
// sync, the expiry reaper). They must never wear the red self-grant flag —
// that flag exists for a HUMAN granting themselves access, and burying it in
// automation noise defeats it (live-audit finding).
function isSystemEvent(e) {
    return e.reason.startsWith('auto-granted') || e.reason.startsWith('roster:') || e.actorUserId.startsWith('system');
}
const KIND_VARIANTS = {
    granted: 'secondary',
    revoked: 'outline',
    extended: 'secondary',
    break_glass_used: 'destructive',
    designee_granted: 'secondary',
    expired: 'outline'
};
const EVENT_KINDS = Object.keys(KIND_VARIANTS);
export function GrantHistoryPanel() {
    const [subjectDraft, setSubjectDraft] = useState('');
    const [actorDraft, setActorDraft] = useState('');
    const [selectedKinds, setSelectedKinds] = useState(new Set());
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [pageToken, setPageToken] = useState('');
    const [tokenStack, setTokenStack] = useState([]);
    const subjectUserId = useDebounced(subjectDraft);
    const actorUserId = useDebounced(actorDraft);
    const kindsKey = [
        ...selectedKinds
    ].sort().join(',');
    const isFiltered = Boolean(subjectUserId || actorUserId || selectedKinds.size > 0 || dateFrom || dateTo);
    // A filter change invalidates the current page — offset tokens from the
    // PREVIOUS filter set point at different rows under the new one.
    useEffect(()=>{
        setPageToken('');
        setTokenStack([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        subjectUserId,
        actorUserId,
        kindsKey,
        dateFrom,
        dateTo
    ]);
    const filters = {
        subjectUserId,
        actorUserId,
        eventKinds: selectedKinds.size > 0 ? [
            ...selectedKinds
        ] : undefined,
        createdAfter: dateFrom ? Timestamp.fromDate(new Date(`${dateFrom}T00:00:00`)) : undefined,
        createdBefore: dateTo ? Timestamp.fromDate(new Date(`${dateTo}T23:59:59`)) : undefined
    };
    const { data, isLoading, error } = useListGrantEvents(filters, pageToken);
    const events = data?.events;
    const pagination = data?.pagination;
    const toggleKind = (kind)=>{
        setSelectedKinds((prev)=>{
            const next = new Set(prev);
            if (next.has(kind)) next.delete(kind);
            else next.add(kind);
            return next;
        });
    };
    const goNext = ()=>{
        const next = pagination?.nextPageToken;
        if (!next) return;
        setTokenStack((prev)=>[
                ...prev,
                pageToken
            ]);
        setPageToken(next);
    };
    const goPrev = ()=>{
        if (tokenStack.length === 0) return;
        setPageToken(tokenStack[tokenStack.length - 1]);
        setTokenStack((prev)=>prev.slice(0, -1));
    };
    // Actor/subject names come pre-resolved on the wire (SAD-208) — this panel
    // is mounted on /admin/activity, reachable by non-admin grantors and
    // access_audit:read capability holders, and the admin-gated
    // ResolveUserNames RPC 403s for them. Bundle/group names are catalogs this
    // console already loads admin-side, so useAdminNames still covers those.
    const names = useAdminNames();
    const subjectCell = (e)=>{
        if (e.subjectUserId) {
            return /*#__PURE__*/ _jsx(ResolvedId, {
                id: e.subjectUserId,
                name: e.subjectDisplayName
            });
        }
        if (e.subjectGroupId) {
            return /*#__PURE__*/ _jsxs("span", {
                children: [
                    /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "mr-1",
                        children: "group"
                    }),
                    /*#__PURE__*/ _jsx(ResolvedId, {
                        id: e.subjectGroupId,
                        name: names.group(e.subjectGroupId)
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "—"
        });
    };
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.adminAccess.historyPanel,
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                children: [
                    /*#__PURE__*/ _jsxs(CardTitle, {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(History, {
                                className: "h-4 w-4"
                            }),
                            " Grant history"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CardDescription, {
                        children: "Every grant, revoke, and break-glass use in this tenant — newest first. Self-grants are flagged."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "history-subject-filter",
                                        children: "Subject"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        id: "history-subject-filter",
                                        "data-testid": Testids.adminAccess.historySubjectFilterInput,
                                        placeholder: "Subject user id",
                                        className: "w-48",
                                        value: subjectDraft,
                                        onChange: (e)=>setSubjectDraft(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "history-actor-filter",
                                        children: "Actor"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        id: "history-actor-filter",
                                        "data-testid": Testids.adminAccess.historyActorFilterInput,
                                        placeholder: "Actor user id",
                                        className: "w-48",
                                        value: actorDraft,
                                        onChange: (e)=>setActorDraft(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "history-date-from",
                                        children: "From"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        id: "history-date-from",
                                        type: "date",
                                        "data-testid": Testids.adminAccess.historyDateFromInput,
                                        value: dateFrom,
                                        onChange: (e)=>setDateFrom(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "history-date-to",
                                        children: "To"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        id: "history-date-to",
                                        type: "date",
                                        "data-testid": Testids.adminAccess.historyDateToInput,
                                        value: dateTo,
                                        onChange: (e)=>setDateTo(e.target.value)
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: "flex flex-wrap items-center gap-3 rounded-md border p-2",
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                className: "px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                                children: "Event kind"
                            }),
                            EVENT_KINDS.map((kind)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Checkbox, {
                                            id: `history-kind-${kind}`,
                                            "data-testid": Testids.adminAccess.historyKindFilterOption,
                                            "data-kind": kind,
                                            checked: selectedKinds.has(kind),
                                            onCheckedChange: ()=>toggleKind(kind)
                                        }),
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: `history-kind-${kind}`,
                                            className: "font-normal",
                                            children: kind
                                        })
                                    ]
                                }, kind))
                        ]
                    }),
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Loading…"
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        className: "text-destructive text-sm",
                        children: error instanceof Error ? error.message : 'Failed to load history'
                    }),
                    events && events.length === 0 && isFiltered && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        "data-testid": Testids.adminAccess.historyFilteredEmptyState,
                        children: "No grant activity matches these filters."
                    }),
                    events && events.length === 0 && !isFiltered && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "No grant activity yet."
                    }),
                    events && events.length > 0 && /*#__PURE__*/ _jsxs(Table, {
                        children: [
                            /*#__PURE__*/ _jsx(TableHeader, {
                                children: /*#__PURE__*/ _jsxs(TableRow, {
                                    children: [
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "When"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Event"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Actor → Subject"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Bundle"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Type"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Reason"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableBody, {
                                children: events.map((e)=>{
                                    const system = isSystemEvent(e);
                                    const humanSelfGrant = e.selfGrant && !system;
                                    return /*#__PURE__*/ _jsxs(TableRow, {
                                        "data-testid": Testids.adminAccess.historyRow,
                                        "data-self-grant": humanSelfGrant ? 'true' : undefined,
                                        className: humanSelfGrant ? 'bg-destructive/10' : undefined,
                                        children: [
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                className: "text-xs whitespace-nowrap",
                                                children: formatTimestamp(e.createdAt)
                                            }),
                                            /*#__PURE__*/ _jsxs(TableCell, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(Badge, {
                                                        variant: KIND_VARIANTS[e.eventKind] ?? 'secondary',
                                                        children: e.eventKind
                                                    }),
                                                    system && /*#__PURE__*/ _jsx(Badge, {
                                                        variant: "outline",
                                                        className: "ml-1 text-muted-foreground",
                                                        title: "Created automatically (program seed, roster sync, or expiry sweep) — not a human action",
                                                        children: "system"
                                                    }),
                                                    humanSelfGrant && /*#__PURE__*/ _jsx(Badge, {
                                                        variant: "destructive",
                                                        className: "ml-1",
                                                        "data-testid": Testids.adminAccess.historySelfGrantBadge,
                                                        children: "self-grant"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(TableCell, {
                                                className: "text-xs",
                                                children: [
                                                    /*#__PURE__*/ _jsx(ResolvedId, {
                                                        id: e.actorUserId,
                                                        name: e.actorDisplayName
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-muted-foreground",
                                                        children: " → "
                                                    }),
                                                    subjectCell(e)
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                className: "text-xs",
                                                children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                    id: e.roleBundleKey,
                                                    name: names.bundle(e.roleBundleKey)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                children: /*#__PURE__*/ _jsx(Badge, {
                                                    variant: e.assignmentType === 'break_glass' ? 'destructive' : 'secondary',
                                                    children: e.assignmentType
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                className: "text-muted-foreground max-w-64 truncate text-xs",
                                                title: e.reason,
                                                children: e.reason || '—'
                                            })
                                        ]
                                    }, e.id);
                                })
                            })
                        ]
                    }),
                    events && events.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    pagination?.totalCount ?? events.length,
                                    " total"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "outline",
                                        size: "sm",
                                        "data-testid": Testids.adminAccess.historyPrevPageButton,
                                        disabled: tokenStack.length === 0,
                                        onClick: goPrev,
                                        children: "Previous"
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "outline",
                                        size: "sm",
                                        "data-testid": Testids.adminAccess.historyNextPageButton,
                                        disabled: !pagination?.nextPageToken,
                                        onClick: goNext,
                                        children: "Next"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
